import * as React from 'react';
// @tidal-export alias=./GlobalControlsProvider

import { validateTidalLicense } from '@workwave-tidal/license-utils'
import { type ReactElement, createContext, useContext, useState } from 'react'

export type ControlsPlacement = 'left' | 'top' | 'right' | 'bottom'

export type GlobalControls = {
  controls: {
    left: ReactElement[]
    top: ReactElement[]
    right: ReactElement[]
    bottom: ReactElement[]
  }
}

export type GlobalControlsApi = {
  registerControl: (placement: ControlsPlacement, ...elements: ReactElement[]) => () => void
}

// ------------------------------------------------------------------------
// Tidal Stories State
// ------------------------------------------------------------------------

const globalControlsContext = createContext<GlobalControls>({
  controls: {
    left: [],
    top: [],
    right: [],
    bottom: [],
  },
})

const globalControlsApiContext = createContext<GlobalControlsApi>({
  registerControl: () => () => {},
})

// ------------------------------------------------------------------------
//  Context Provider
// ------------------------------------------------------------------------

type Props = {
  children: ReactElement | ReactElement[]
}

export function GlobalControlsProvider(props: Props) {
  const { children } = props

  const [controls, setControls] = useState<GlobalControls['controls']>({
    left: [],
    top: [],
    right: [],
    bottom: [],
  })

  const [api] = useState<GlobalControlsApi>(() => ({
    registerControl: (placement: ControlsPlacement, ...elements: ReactElement[]) => {
      setControls(prev => {
        const next = { ...prev }
        next[placement] = [...next[placement], ...elements]
        return next
      })

      return () => {
        setControls(prev => {
          const next = { ...prev }
          next[placement] = next[placement].filter(item => !elements.includes(item))
          return next
        })
      }
    },
  }))

  return (
    <globalControlsApiContext.Provider value={api}>
      <globalControlsContext.Provider value={{ controls }}>{children}</globalControlsContext.Provider>
    </globalControlsApiContext.Provider>
  )
}

// ------------------------------------------------------------------------
// Tidal Stories Context hook
// ------------------------------------------------------------------------

export function useGlobalControls() {
  return useContext(globalControlsContext)
}

export function useGlobalControlsApi() {
  return useContext(globalControlsApiContext)
}

// #license-validation
validateTidalLicense()
